import React, { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription 
} from "@/Components/ui/dialog";
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import { toast } from "@/hooks/use-toast";

export function useCommandMenu() {
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [demoFileName, setDemoFileName] = useState("demo-project");
  const [isLoading, setIsLoading] = useState(false);

  const openDemoProjectDialog = () => {
    setDemoFileName("demo-project");
    setDemoDialogOpen(true);
  };

  const handleImportDemo = async () => {
    if (!demoFileName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a demo project name",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const fileName = demoFileName.endsWith(".json") 
        ? demoFileName 
        : `${demoFileName}.json`;
      
      const response = await fetch(`Projects/ImportDemo?fileName=${encodeURIComponent(fileName)}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to import demo project (${response.status})`);
      }

      await response.json(); // Get the new project ID
      
      toast({
        title: "Success",
        description: "Demo project imported successfully!",
      });

      setDemoDialogOpen(false);
      
      // Reload to refresh project list
      window.location.reload();
      
    } catch (error) {
      console.error("Error importing demo project:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to import demo project",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isLoading) {
      handleImportDemo();
    }
  };

  const DemoProjectDialog = () => (
    <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Import Demo Project</DialogTitle>
          <DialogDescription>
            Enter the name of the demo project file to import. The file should be located in the server's DemoProjects folder.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="demo-file-name" className="text-sm font-medium">
              Demo File Name
            </label>
            <Input
              id="demo-file-name"
              value={demoFileName}
              onChange={(e) => setDemoFileName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="demo-project"
              disabled={isLoading}
              autoFocus
            />
            <p className="text-sm text-muted-foreground">
              .json extension will be added automatically
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setDemoDialogOpen(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleImportDemo}
            disabled={isLoading}
          >
            {isLoading ? "Importing..." : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return {
    openDemoProjectDialog,
    DemoProjectDialog,
  };
}
